import { useQuery } from '@tanstack/react-query';
import { fetchSiteProfessions, type ISiteProfession } from '@apis/professions';
import { siteProfessionsKey } from '@constants/queryKeys';

// Stable empty reference for the loading/error/unconfigured paths — returning a
// fresh `[]` each render would re-trigger every consumer's `useMemo`/`memo`.
const EMPTY_PROFESSIONS: readonly ISiteProfession[] = [];

/**
 * Live working-professions catalog for a site — the option source for the
 * Scheduler task-profession picker. Backed by
 * `GET /api/sites/:siteId/analytics/professions` (Trusstor-sourced). On loading,
 * error, or an unconfigured site it returns an EMPTY array so the picker degrades
 * to its disabled empty-state hint instead of erroring — the picker keys its
 * enabled state off `length`. Generous staleTime (the catalog changes rarely),
 * mirroring `useSiteLocations`.
 */
export function useSiteProfessions(siteId: string): readonly ISiteProfession[] {
  const { data } = useQuery({
    queryKey: siteProfessionsKey(siteId),
    queryFn: ({ signal }) => fetchSiteProfessions(siteId, signal),
    enabled: siteId !== '',
    staleTime: 30 * 60_000,
    retry: false,
  });
  return data ?? EMPTY_PROFESSIONS;
}
